import { useLocation } from "react-router-dom";
import { isDesktopBrowserLayout } from "../../../common/hooks/useIsDesktopView";
import { useCurrentUser } from "../../user/hooks/useCurrentUser";
import { isPublicBrowserPath } from "../publicBrowserPaths";
import {
  useDesktopCodeRequest,
  type DesktopCodeRequest,
} from "../stores/desktopCodeRequestStore";

/**
 * The code the desktop auth gate is waiting for over this route, or `null`
 * when no gate stands in front of it.
 *
 * The gate is the code half of the desktop door: `/signup` or `/login` takes the address
 * and asks for the code (`desktopCodeRequestStore`), then shows the app the
 * visitor is signing into with this modal over it. Without a request there is
 * nothing to wait for, and the logged-out guard sends the visitor to `/signup`
 * instead.
 *
 * Two places need the answer and they are not parent and child in a way that
 * could pass it down: `AppRoutes` mounts the gate, and `AppLayout` — its
 * ancestor — has to make the app behind it inert. One implementation rather
 * than two copies of a predicate that must agree exactly, or the shell goes
 * inert on a page with no gate over it.
 *
 * Keyed on the FIREBASE session (`isAuthenticated`), never on
 * `currentUser === null`: a failed `/users/me` also yields null, and treating
 * an API hiccup as "signed out" is what puts a login wall in front of a
 * signed-in reader. A verified login code signs in, which takes the gate down
 * before `loginAndRedirect` moves on.
 */
export function useDesktopAuthGateRequest(): DesktopCodeRequest | null {
  const location = useLocation();
  const { isLoading, isAuthenticated } = useCurrentUser();
  const request = useDesktopCodeRequest();

  return isDesktopBrowserLayout() &&
    !isPublicBrowserPath(location.pathname) &&
    !isAuthenticated &&
    !isLoading
    ? request
    : null;
}
